/**
 * Workflow Selectors — Derives the current clinical workflow stage
 * and pipeline readiness from Redux state.
 *
 * Stages: Record → Correct → CPT Detect → Generate → Review
 *
 * Auto-advancement is purely selector-driven — no manual navigation.
 */
import type { RootState } from './index';
import type { CPTExamState } from './cptExamSlice';

// ── Workflow Stage ──────────────────────────────────────────────────

export type WorkflowStage = 'record' | 'correct' | 'cpt_detect' | 'generate' | 'review';

export type StepStatus = 'inactive' | 'active' | 'completed' | 'error' | 'waiting';

export const WORKFLOW_STEPS: { key: WorkflowStage; label: string }[] = [
  { key: 'record', label: 'Record' },
  { key: 'correct', label: 'Correct' },
  { key: 'cpt_detect', label: 'CPT Exam' },
  { key: 'generate', label: 'Generate' },
  { key: 'review', label: 'Review' },
];

export const STAGE_INDEX: Record<WorkflowStage, number> = {
  record: 0,
  correct: 1,
  cpt_detect: 2,
  generate: 3,
  review: 4,
};

// ── Pipeline Readiness ──────────────────────────────────────────────

export interface PipelineReadiness {
  pipelineReady: boolean;
  transcriptFinalized: boolean;
  correctionCompleted: boolean;
  cptExamResolved: boolean;
  /** @deprecated Use cptExamResolved instead */
  classificationAvailable: boolean;
  templateResolved: boolean;
  kgStatus: 'ready' | 'disabled' | 'unavailable';
  pipelineHealth: 'ready' | 'degraded' | 'blocked';
  blockReasons: string[];
  provider: string | null;
  templateName: string | null;
}

// ── Core Selector: Active Workflow Stage ────────────────────────────

export function selectWorkflowStage(state: RootState): WorkflowStage {
  const t = state.transcription;
  const r = state.report;

  // Stage 5: Report generated → Review
  if (r.report !== null) {
    return 'review';
  }

  // Stage 4: CPT exam resolved (manual or auto-detected) → Generate
  const cptExam = (state as RootState & { cptExams: CPTExamState }).cptExams?.selectedExam;
  if (cptExam !== null && cptExam !== undefined) {
    return 'generate';
  }

  // Stage 3: Correction completed → CPT Detect
  if (
    t.finalizedCorrectedText.length > 0 &&
    t.pendingCorrections === 0 &&
    !t.isRefiningInFlight &&
    !t.isIncrementalCorrecting
  ) {
    return 'cpt_detect';
  }

  // Stage 2: Transcript finalized → Correct
  if (t.isFinal && t.finalizedText.length > 0) {
    return 'correct';
  }

  // Stage 1: Default → Record
  return 'record';
}

// ── Step Status for Each Stage ──────────────────────────────────────

export function selectStepStatuses(state: RootState): StepStatus[] {
  const t = state.transcription;
  const r = state.report;
  const activeStage = selectWorkflowStage(state);
  const activeIdx = STAGE_INDEX[activeStage];

  const statuses: StepStatus[] = WORKFLOW_STEPS.map((step, idx) => {
    if (idx < activeIdx) return 'completed';
    if (idx === activeIdx) return 'active';
    return 'inactive';
  });

  // Overlay error / waiting states

  // Record: error if session failed to create
  if (t.error && activeStage === 'record') {
    statuses[0] = 'error';
  }

  // Correct: waiting if corrections in-flight
  if (
    activeStage === 'correct' &&
    (t.pendingCorrections > 0 || t.isRefiningInFlight || t.isIncrementalCorrecting)
  ) {
    statuses[1] = 'waiting';
  }

  // CPT Detect: waiting if CPT detection in progress
  const cptState = (state as RootState & { cptExams: CPTExamState }).cptExams;
  if (activeStage === 'cpt_detect' && cptState?.detecting) {
    statuses[2] = 'waiting';
  }

  // Generate: error if report generation failed
  if (r.error && activeStage === 'generate') {
    statuses[3] = 'error';
  }

  // Generate: waiting if report is being generated
  if (r.loading && activeStage !== 'review') {
    statuses[3] = 'waiting';
  }

  return statuses;
}

// ── Pipeline Readiness Check ────────────────────────────────────────

export function selectPipelineReadiness(state: RootState): PipelineReadiness {
  const t = state.transcription;
  const health = state.systemHealth.data;
  const blockReasons: string[] = [];

  // 1. Transcript finalized
  const transcriptFinalized = t.isFinal && t.finalizedText.length > 0;
  if (!transcriptFinalized) blockReasons.push('Transcript not finalized');

  // 2. Correction completed — either corrected text exists, or session is final with raw text
  // (correction may have been skipped/unavailable for very short dictations)
  const hasText = t.finalizedCorrectedText.length > 0 || (t.isFinal && t.finalizedText.length > 0);
  const correctionCompleted =
    hasText &&
    t.pendingCorrections === 0 &&
    !t.isRefiningInFlight;
  if (!correctionCompleted) blockReasons.push('AI correction incomplete');

  // 3. CPT exam resolved (manual or auto-detected)
  const cptExam = (state as RootState & { cptExams: CPTExamState }).cptExams?.selectedExam;
  const cptState = (state as RootState & { cptExams: CPTExamState }).cptExams;
  const cptExamResolved = cptExam !== null && cptExam !== undefined;
  // Classification is no longer required — CPT exam drives template selection
  const classificationAvailable = t.classification.category !== null || cptExamResolved;
  // CPT exam is a soft warning — if detection is still running, surface that; if failed/missing, warn but don't block
  if (!cptExamResolved && cptState?.detecting) blockReasons.push('CPT exam detection in progress');
  else if (!cptExamResolved) blockReasons.push('No CPT exam selected — a generic template will be used');

  // 4. Template resolved — always true: generic fallback used when no CPT exam selected
  const templateResolved = true;

  // 5. KG status — derived from system health modules
  const kgModule = health?.modules?.['knowledge_graph'];
  let kgStatus: PipelineReadiness['kgStatus'] = 'unavailable';
  if (kgModule) {
    kgStatus = kgModule.status === 'healthy' ? 'ready' : 'disabled';
  }
  // KG is optional — don't block generation for it

  // 6. Pipeline health — derived from system health
  let pipelineHealth: PipelineReadiness['pipelineHealth'] = 'ready';
  if (health) {
    if ((health.down_services?.length ?? 0) > 0) {
      pipelineHealth = 'blocked';
      blockReasons.push(`Critical services down: ${health.down_services.join(', ')}`);
    } else if ((health.degraded_services?.length ?? 0) > 0) {
      pipelineHealth = 'degraded';
    }
  }

  // Provider info
  const reportModule = health?.modules?.['report'];
  const provider = reportModule?.active_provider ?? null;

  // Template name from CPT exam or classification
  const templateName = cptExam
    ? cptExam.exam_name
    : t.classification.category
      ? t.classification.category.toLowerCase().replace(/\s+/g, '_')
      : null;

  const pipelineReady =
    transcriptFinalized &&
    correctionCompleted &&
    pipelineHealth !== 'blocked';

  return {
    pipelineReady,
    transcriptFinalized,
    correctionCompleted,
    cptExamResolved,
    classificationAvailable,
    templateResolved,
    kgStatus,
    pipelineHealth,
    blockReasons,
    provider,
    templateName,
  };
}

// ── Generate Button State ───────────────────────────────────────────

export type GenerateButtonState = 'not_ready' | 'ready' | 'generating';

export function selectGenerateButtonState(state: RootState): GenerateButtonState {
  const r = state.report;
  if (r.loading) return 'generating';

  const readiness = selectPipelineReadiness(state);
  return readiness.pipelineReady ? 'ready' : 'not_ready';
}

// ── Dictation Complete Detection ────────────────────────────────────

export function selectDictationJustCompleted(state: RootState): boolean {
  const t = state.transcription;
  const r = state.report;
  return (
    t.isFinal &&
    t.finalizedText.length > 0 &&
    t.pendingCorrections === 0 &&
    !t.isRefiningInFlight &&
    r.report === null &&
    !r.loading
  );
}
